import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorText } from '../lib/api';
import { hasRole, isStaff, useMe } from '../lib/session';
import { watchLive, type RoomView, type TokenResult } from '../lib/live';
import { Button, Card, CardHeader, Empty, ErrorText, Field, Input, PageHeader, Pill } from '../components/ui';
import { LiveJoin } from '../components/LiveJoin';
import { RecordingsCard } from '../components/Recordings';

interface RoomsData {
  livekit: { configured: boolean };
  rooms: RoomView[];
}

function NotSetUp() {
  return (
    <p role="status" className="m-0 rounded-lg bg-warn-bg px-3 py-2 text-sm text-warn">
      Live video isn’t set up yet. Ask the floor admin to connect LiveKit. You can still see who is in each room.
    </p>
  );
}

function RoomCard({ r }: { r: RoomView }) {
  return (
    <Link to={`/rooms/${r.id}`} className="block text-ink no-underline hover:text-ink">
      <Card className="flex h-full flex-col gap-2 p-4 transition-colors hover:border-line2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <span className="text-base font-semibold">{r.name}</span>
          <Pill tone={r.kind === 'DESK' ? 'accent' : 'live'}>{r.kind === 'DESK' ? 'Desk room' : 'Ad-hoc'}</Pill>
        </div>
        {r.context && <span className="text-xs text-muted">About a {r.context.type.toLowerCase()}</span>}
        <span className="text-sm text-sub">{r.participants.length ? `${r.participants.length} in the room: ${r.participants.map((p) => p.name).join(', ')}` : 'Nobody here yet'}</span>
        <span className="mt-auto text-xs text-muted">{r.shareModeLabel}</span>
      </Card>
    </Link>
  );
}

interface Person {
  id: string;
  name: string;
}

function NewRoom({ onDone }: { onDone: (id: string) => void }) {
  const [name, setName] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const { data: people } = useQuery({ queryKey: ['members-lite'], queryFn: () => api<Person[]>('/live/people').catch(() => [] as Person[]), staleTime: 300_000 });
  const create = useMutation({ mutationFn: () => api<RoomView>('/rooms', { body: { name, memberIds: picked } }), onSuccess: (r) => onDone(r.id) });
  return (
    <Card>
      <CardHeader title="Open a room" />
      <form
        className="flex flex-col gap-3 p-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <Field label="Room name">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Gold review" maxLength={80} required />
        </Field>
        <fieldset className="m-0 flex flex-col gap-1.5 border-0 p-0">
          <legend className="mb-1 text-xs text-muted">Invite</legend>
          <div className="flex max-h-44 flex-col gap-1 overflow-y-auto">
            {people?.map((p) => (
              <label key={p.id} className="flex min-h-9 items-center gap-2 text-sm">
                <input type="checkbox" checked={picked.includes(p.id)} onChange={(e) => setPicked((v) => (e.target.checked ? [...v, p.id] : v.filter((x) => x !== p.id)))} />
                {p.name}
              </label>
            ))}
            {!people?.length && <span className="text-sm text-muted">You can invite people after the room is open.</span>}
          </div>
        </fieldset>
        <ErrorText error={create.error} />
        <div>
          <Button variant="primary" type="submit" disabled={create.isPending || name.trim().length < 2}>
            Open room
          </Button>
        </div>
      </form>
    </Card>
  );
}

export function RoomsList() {
  const me = useMe();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['rooms'], queryFn: () => api<RoomsData>('/rooms'), refetchInterval: 30_000 });
  useEffect(() => watchLive({ kind: 'room', id: 'floor' }, () => void qc.invalidateQueries({ queryKey: ['rooms'] })), [qc]);
  const desks = data?.rooms.filter((r) => r.kind === 'DESK') ?? [];
  const adhoc = data?.rooms.filter((r) => r.kind === 'ADHOC') ?? [];
  const staff = isStaff(me.data?.roles);
  return (
    <>
      <PageHeader title="Rooms" sub="Talk and share a screen with the desk · one room per market, plus rooms you open" right={staff && <Button variant="primary" onClick={() => setAdding((v) => !v)}>{adding ? 'Close' : '+ Open a room'}</Button>} />
      {data && !data.livekit.configured && <NotSetUp />}
      {adding && <NewRoom onDone={(id) => nav(`/rooms/${id}`)} />}
      <section aria-label="Desk rooms" className="flex flex-col gap-2">
        <h2 className="m-0 text-sm font-semibold text-sub">Desk rooms</h2>
        <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-3">
          {isLoading && <Card className="p-4 text-sub">Loading…</Card>}
          {desks.map((r) => <RoomCard key={r.id} r={r} />)}
        </div>
      </section>
      <section aria-label="Your rooms" className="flex flex-col gap-2">
        <h2 className="m-0 text-sm font-semibold text-sub">Ad-hoc rooms</h2>
        <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-3">
          {!isLoading && !adhoc.length && (
            <Card className="sm:col-span-2 xl:col-span-3">
              <Empty>No ad-hoc rooms right now. Use “Discuss live” on a trade, setup or analysis to open one.</Empty>
            </Card>
          )}
          {adhoc.map((r) => <RoomCard key={r.id} r={r} />)}
        </div>
      </section>
    </>
  );
}

export function RoomPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data, error } = useQuery({ queryKey: ['room', id], queryFn: () => api<{ livekit: { configured: boolean }; room: RoomView }>(`/rooms/${id}`), refetchInterval: 20_000 });
  useEffect(() => watchLive({ kind: 'room', id }, () => void qc.invalidateQueries({ queryKey: ['room', id] })), [id, qc]);
  const refresh = () => qc.invalidateQueries({ queryKey: ['room', id] });
  const act = useMutation({ mutationFn: (v: { path: string; body?: object }) => api(`/rooms/${id}/${v.path}`, { body: v.body ?? {} }), onSuccess: refresh });
  const close = useMutation({ mutationFn: () => api(`/rooms/${id}/close`, { body: {} }), onSuccess: () => nav('/rooms') });
  const me = useMe();
  const [invite, setInvite] = useState<string[]>([]);
  const { data: people } = useQuery({ queryKey: ['members-lite'], queryFn: () => api<Person[]>('/live/people').catch(() => [] as Person[]), staleTime: 300_000, enabled: !!data?.room.canModerate && data.room.kind === 'ADHOC' });
  if (error) return <Card className="p-4"><p className="m-0">{errorText(error)} <Link to="/rooms">Back to rooms</Link></p></Card>;
  if (!data) return <Card className="p-4 text-sub">Loading…</Card>;
  const r = data.room;
  const mod = r.canModerate;
  const canPublishNow = r.shareMode === 'OPEN' || mod || r.myShareRequest === 'GRANTED';
  return (
    <>
      <PageHeader back={<Link to="/rooms">← Rooms</Link>} title={r.name} sub={`${r.kind === 'DESK' ? 'Desk room' : 'Ad-hoc room'} · ${r.shareModeLabel}`} right={r.context?.href ? <Link to={r.context.href}>Open the {r.context.type.toLowerCase()} →</Link> : undefined} />
      {!data.livekit.configured && <NotSetUp />}
      <Card className="flex flex-col gap-3 p-4">
        <LiveJoin getToken={() => api<TokenResult>(`/rooms/${id}/token`, { body: {} })} layout="CHART_WEBCAM" label="Join the room" onJoined={refresh} onLeft={refresh} />
        {!canPublishNow && (
          <div className="flex flex-wrap items-center gap-3 text-sm text-sub">
            This room is in “request to share” mode.
            {r.myShareRequest === 'PENDING' ? <Pill tone="warn">Request sent</Pill> : <Button onClick={() => act.mutate({ path: 'share-requests' })} disabled={act.isPending}>Ask to share</Button>}
          </div>
        )}
        <ErrorText error={act.error} />
      </Card>
      <Card>
        <CardHeader title={`In the room (${r.participants.length})`} />
        <ul className="m-0 flex list-none flex-col p-0">
          {!r.participants.length && <li className="p-4 text-sm text-muted">Nobody is in the room.</li>}
          {r.participants.map((p) => (
            <li key={p.identity} className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-2.5 last:border-b-0">
              <span>
                {p.name} {p.sharing && <Pill tone="live">Sharing</Pill>}
              </span>
              {mod && p.identity !== me.data?.id && (
                <span className="flex flex-wrap gap-2">
                  <Button onClick={() => act.mutate({ path: `participants/${p.identity}`, body: { action: 'MUTE' } })}>Mute</Button>
                  {p.sharing && <Button onClick={() => act.mutate({ path: `participants/${p.identity}`, body: { action: 'STOP_SHARE' } })}>Stop share</Button>}
                  <Button variant="danger" onClick={() => act.mutate({ path: `participants/${p.identity}`, body: { action: 'REMOVE' } })}>Remove</Button>
                </span>
              )}
            </li>
          ))}
        </ul>
      </Card>
      {mod && hasRole(me.data?.roles, 'MASTER_TRADER', 'ADMIN', 'RISK_MANAGER') && <RecordingsCard roomId={id} live />}
      {mod && (
        <Card>
          <CardHeader title="Moderation" />
          <div className="flex flex-col gap-3 p-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-sub">Screen sharing</span>
              <Button variant={r.shareMode === 'OPEN' ? 'primary' : 'secondary'} onClick={() => act.mutate({ path: 'mode', body: { shareMode: 'OPEN' } })}>Anyone can share</Button>
              <Button variant={r.shareMode === 'REQUEST' ? 'primary' : 'secondary'} onClick={() => act.mutate({ path: 'mode', body: { shareMode: 'REQUEST' } })}>Request to share</Button>
            </div>
            {r.pendingRequests.map((q) => (
              <div key={q.userId} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-raised px-3 py-2">
                <span>{q.name} asks to share</span>
                <span className="flex gap-2">
                  <Button variant="success" onClick={() => act.mutate({ path: `share-requests/${q.userId}`, body: { decision: 'GRANT' } })}>Allow</Button>
                  <Button onClick={() => act.mutate({ path: `share-requests/${q.userId}`, body: { decision: 'DENY' } })}>Decline</Button>
                </span>
              </div>
            ))}
            {r.grantedShares.map((g) => (
              <div key={g.userId} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span>{g.name} may share</span>
                <Button variant="ghost" onClick={() => act.mutate({ path: `share-requests/${g.userId}`, body: { decision: 'REVOKE' } })}>Take back</Button>
              </div>
            ))}
            {r.kind === 'ADHOC' && (
              <>
                <div className="flex flex-col gap-1 text-sm">
                  <span className="text-xs text-muted">Members: {r.members?.map((m) => m.name).join(', ') || 'none yet'}</span>
                  <div className="flex max-h-32 flex-col overflow-y-auto">
                    {people?.filter((p) => !r.members?.some((m) => m.id === p.id)).map((p) => (
                      <label key={p.id} className="flex min-h-9 items-center gap-2">
                        <input type="checkbox" checked={invite.includes(p.id)} onChange={(e) => setInvite((v) => (e.target.checked ? [...v, p.id] : v.filter((x) => x !== p.id)))} />
                        {p.name}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button disabled={!invite.length} onClick={() => { act.mutate({ path: 'invite', body: { memberIds: invite } }); setInvite([]); }}>Invite selected</Button>
                  <Button variant="danger" onClick={() => close.mutate()}>Close room</Button>
                </div>
              </>
            )}
          </div>
        </Card>
      )}
    </>
  );
}

/** "Discuss live" for a trade, setup or analysis (INT-SCR-03): opens or reuses the linked room. */
export function DiscussLive({ type, id }: { type: 'TRADE' | 'SETUP' | 'ANALYSIS'; id: string }) {
  const nav = useNavigate();
  const m = useMutation({ mutationFn: () => api<RoomView>('/rooms/discuss', { body: { contextType: type, contextId: id } }), onSuccess: (r) => nav(`/rooms/${r.id}`) });
  return (
    <span className="inline-flex flex-col gap-1">
      <Button onClick={() => m.mutate()} disabled={m.isPending}>{m.isPending ? 'Opening…' : 'Discuss live'}</Button>
      {m.error && <span role="alert" className="text-xs text-down">{errorText(m.error)}</span>}
    </span>
  );
}

export const canOpenRooms = (roles: string[] | undefined) => hasRole(roles, 'MASTER_TRADER', 'RISK_MANAGER', 'ADMIN');
